/* ============================================================================
   header.js — CircleDrops
   Tablet gender-switcher clone + mobile gender toggle
   + mobile-search back-arrow fix
   + iOS background scroll lock while the search is open.

   ── THE BACK-ARROW FIX (root cause) ──────────────────────────────────────
   The theme ALREADY draws a reliable back arrow on the mobile full-screen
   search, using its own icon font:

       #search > a.toggle:before { content: "\e907"; }   (async-search.css)

   Icon-font glyphs paint reliably on every browser. But header.liquid HID
   that glyph and replaced it with a base64 background-IMAGE, which some
   mobile browsers fail to paint at random. This file injects a small
   stylesheet that restores the theme's own icon-font chevron at a proper,
   tappable size.

   ── THE iOS SCROLL-LOCK FIX (this update) ────────────────────────────────
   PROBLEM: on iOS Safari, when the full-screen search is open (keyboard up),
   the page BEHIND the overlay still scrolls. The CSS lock in livesearch.liquid
   (html:has([data-cds-search]){overflow:hidden}) works on desktop but iOS
   Safari simply ignores overflow:hidden for touch scrolling — so the body
   keeps moving behind the modal.

   THE RELIABLE iOS FIX is to make the <body> position:fixed while the search
   is open (which genuinely freezes the page on iOS), remembering the current
   scroll position and restoring it on close. That requires JS — and this
   external script runs, whereas the livesearch.liquid markup is injected via
   innerHTML where its <script> would not. So the lock lives here.
   ============================================================================ */

(function () {

    /* ============================================================
       0. Restore the theme's native back-arrow glyph (mobile).
       ============================================================ */
    function injectBackArrowStyle() {
      if (document.getElementById('cds-back-arrow-style')) return;

      var css =
        '@media only screen and (max-width:760px){' +
          '#root #search > a.toggle{' +
            'background-image:none !important;' +
            'color:var(--custom_top_search_fg,#17151F) !important;' +
          '}' +
          '#root #search > a.toggle:before{' +
            'content:"\\e907" !important;' +
            'display:block !important;' +
            'font-family:i !important;' +
            'font-weight:400 !important;' +
            'font-size:18px !important;' +
            'line-height:20px !important;' +
            'color:var(--custom_top_search_fg,#17151F) !important;' +
            'opacity:1 !important;' +
          '}' +
        '}';

      var style = document.createElement('style');
      style.id = 'cds-back-arrow-style';
      style.type = 'text/css';
      style.appendChild(document.createTextNode(css));
      (document.head || document.documentElement).appendChild(style);
    }

    /* ============================================================
       1. TABLET: Move #gender-switcher-tablet into the header
          right after the search icon.
       ============================================================ */
    function placeTabletGenderSwitcher() {
      if (window.innerWidth < 761 || window.innerWidth > 1000) return;

      var clone = document.getElementById('gender-switcher-tablet');
      if (!clone) return;

      var searchIcon = document.querySelector('.link-btn a.search-compact');
      if (searchIcon && searchIcon.parentNode) {
        searchIcon.parentNode.insertBefore(clone, searchIcon.nextSibling);
        clone.style.display = 'flex';
      }
    }

    /* ============================================================
       2. MOBILE: Hide the in-form gender dropdown when user types.
       ============================================================ */
    function initGenderSwitcherToggle() {
      var input = document.getElementById('search_main');
      var switcher = document.querySelector('#search > .gender-switcher');

      if (!input || !switcher) return;

      function toggle() {
        if (input.value.trim().length > 0) {
          switcher.style.display = 'none';
        } else {
          switcher.style.display = '';
        }
      }

      input.addEventListener('input', toggle);
      toggle();
    }

    /* ============================================================
       3. MOBILE: Tapping the clear (×) button while the full-screen
          search is open fully CLOSES the search (via the theme's own
          back control) instead of leaving an empty centred bar.
       ============================================================ */
    function initClearClosesSearch() {
      /* DESKTOP helper: invoke the theme's OWN "click outside to close"
         (mousedown + click on the body) — but only while the search field
         is EMPTY, so it never interferes the moment the user retypes. */
      function closeIfEmpty() {
        var input = document.getElementById('search_main');
        if (input && input.value.trim().length > 0) return;
        document.body.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
        document.body.dispatchEvent(new MouseEvent('click', { bubbles: true }));
      }

      document.addEventListener('click', function (e) {
        var clear = e.target.closest ? e.target.closest('#search a.clear-toggle') : null;
        if (!clear) return;

        var search = document.getElementById('search');
        if (!search) return;

        var mobile = window.innerWidth <= 760;

        /* ---- MOBILE full-screen: close via the theme's back control
           (clean, no empty centred bar). ---- */
        if (mobile && search.classList.contains('full')) {
          e.preventDefault();
          e.stopImmediatePropagation();

          var input = document.getElementById('search_main');
          if (input) { input.value = ''; }

          var toggle = Array.prototype.filter.call(
            search.querySelectorAll('a.toggle'),
            function (el) { return el.offsetParent !== null; }
          )[0];
          if (toggle) toggle.click();
          return;
        }

        /* ---- DESKTOP: the theme's × empties the field but leaves the
           results dropdown OPEN — and a late predictive-search response
           re-asserts the open state a few hundred ms later, so a single
           close gets overridden. We trigger the theme's own outside-click
           close and repeat it briefly to beat that late response. Each call
           is a no-op once the field has text again, so it never fights a
           user who immediately retypes. ---- */
        [0, 150, 350, 600, 900, 1300, 1700].forEach(function (t) {
          setTimeout(closeIfEmpty, t);
        });
      }, true);
    }

    /* ============================================================
       4. iOS BACKGROUND SCROLL LOCK.
          While the mobile full-screen search is open, freeze the page
          behind it by pinning <body> with position:fixed and offsetting
          it by the current scroll position — the only method iOS Safari
          actually honours. On close, clear the styles and restore the
          exact scroll position.

          The open/closed state is read from the theme's own classes
          (html.search-full / .search-full-mode / .search-focus, or
          #search.full / .has-text) and kept in sync with a
          MutationObserver, so it engages the moment the search opens and
          releases the moment it closes — including when the keyboard is
          up.
       ============================================================ */
    function initScrollLock() {
      var locked = false;
      var savedY = 0;

      /* DESKTOP lock (>=761px): freeze the page with overflow:hidden while the
         results modal is open. Keyed on the theme's own "search-full-mode"
         class, which is added when the modal opens and removed the instant it
         closes — so it locks on open and restores on close automatically, with
         no scroll jump (overflow:hidden keeps the current scroll position).
         scrollbar-gutter:stable reserves the scrollbar's space so the page
         doesn't shift sideways when the scrollbar disappears. Mobile uses the
         position:fixed lock below instead (the only method iOS honours). */
      if (!document.getElementById('cds-scrolllock-style')) {
        var lockCss =
          '@media only screen and (min-width:761px){' +
            'html.search-full-mode{overflow:hidden !important; scrollbar-gutter:stable;}' +
            'html.search-full-mode body{overflow:hidden !important;}' +
          '}';
        var lockStyle = document.createElement('style');
        lockStyle.id = 'cds-scrolllock-style';
        lockStyle.appendChild(document.createTextNode(lockCss));
        (document.head || document.documentElement).appendChild(lockStyle);
      }

      function searchIsOpen() {
        if (window.innerWidth > 760) return false; /* mobile only — desktop is handled by the CSS above */
        /* Key ONLY on the full-screen modal state, which is reliably added
           when the modal opens and REMOVED when it closes. We must NOT use
           "search-focus" or "has-text": those linger on <html>/#search after
           the modal closes, which is exactly what left the page stuck
           non-scrollable. */
        var h = document.documentElement.classList;
        if (h.contains('search-full') || h.contains('search-full-mode')) return true;
        var s = document.getElementById('search');
        return !!(s && s.classList.contains('full'));
      }

      function lock() {
        if (locked) return;
        locked = true;
        savedY = window.scrollY || window.pageYOffset || 0;
        var b = document.body;
        b.style.position = 'fixed';
        b.style.top = (-savedY) + 'px';
        b.style.left = '0';
        b.style.right = '0';
        b.style.width = '100%';
        b.classList.add('cds-scroll-locked');
      }

      function unlock() {
        if (!locked) return;
        locked = false;
        var b = document.body;
        b.style.position = '';
        b.style.top = '';
        b.style.left = '';
        b.style.right = '';
        b.style.width = '';
        b.classList.remove('cds-scroll-locked');
        /* restore the scroll position the page had before we pinned it */
        window.scrollTo(0, savedY);
      }

      function sync() {
        if (searchIsOpen()) { lock(); } else { unlock(); }
      }

      /* React to the theme flipping its search-state classes. */
      try {
        var moHtml = new MutationObserver(sync);
        moHtml.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

        var search = document.getElementById('search');
        if (search) {
          var moSearch = new MutationObserver(sync);
          moSearch.observe(search, { attributes: true, attributeFilter: ['class'] });
        }
      } catch (e) { /* no-op */ }

      /* If the viewport crosses the mobile breakpoint while locked, re-check. */
      window.addEventListener('resize', sync);

      sync();
    }

    /* ============================================================
       5. MOBILE COMPACT HEADER.
          Scrolling DOWN past the top collapses the (already fixed)
          header to one row via html.cds-hdr-compact; scrolling UP
          expands it again. The layout lives in header.liquid.
          Ignored while the full-screen search or a panel/menu is
          open, and while the iOS scroll lock has pinned <body>.
       ============================================================ */
    function initCompactHeader() {
      var mq = window.matchMedia('(max-width: 760px)');
      var html = document.documentElement;
      var DELTA = 8;        /* px of movement before we react */
      var TOP_ZONE = 120;   /* always expanded this close to the top */
      var lastY = window.scrollY || 0;
      var ticking = false;

      function busy() {
        var h = html.classList;
        return h.contains('search-full') || h.contains('search-full-mode') ||
          h.contains('m6pn-open') || h.contains('menu-open') ||
          document.body.classList.contains('cds-scroll-locked');
      }

      function update() {
        ticking = false;
        var y = Math.max(0, window.scrollY || 0);
        if (!mq.matches) {
          html.classList.remove('cds-hdr-compact');
        } else if (!busy()) {
          if (y <= TOP_ZONE) {
            html.classList.remove('cds-hdr-compact');
          } else if (y - lastY > DELTA) {
            html.classList.add('cds-hdr-compact');
          } else if (lastY - y > DELTA) {
            html.classList.remove('cds-hdr-compact');
          } else {
            return; /* small jitter: keep lastY so slow scrolls accumulate */
          }
        }
        lastY = y;
      }

      window.addEventListener('scroll', function () {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      }, { passive: true });
      if (mq.addEventListener) mq.addEventListener('change', update);
    }

    /* ============================================================
       6. Hide count badges that are empty or zero (wishlist in the
          header, cart in the bottom tab bar).
       ============================================================ */
    function initEmptyBadges() {
      function sync(el) {
        var t = (el.textContent || '').trim();
        el.classList.toggle('is-empty', t === '' || t === '0');
      }
      ['wishlist-count', 'tabbar-cart-count'].forEach(function (id) {
        var el = document.getElementById(id);
        if (!el) return;
        sync(el);
        new MutationObserver(function () { sync(el); })
          .observe(el, { childList: true, characterData: true, subtree: true });
      });
    }

    /* ============================================================
       7. DEPARTMENT SWITCHERS on every page.
          The homepage (WOMEN) and mens page (MEN) record the department;
          other pages pre-select the last one visited (default WOMEN).
       ============================================================ */
    function initDepartmentMemory() {
      var KEY = 'cds-dept';
      var MEN_URL = '/pages/mens/mens-odxvlhuw';
      var path = window.location.pathname;
      var current = path === '/' ? 'women' : (path.indexOf('/pages/mens') === 0 ? 'men' : null);

      try {
        if (current) {
          localStorage.setItem(KEY, current);
          return;
        }
        var saved = localStorage.getItem(KEY);
        if (saved !== 'men') return; /* markup already selects WOMEN */
        document.querySelectorAll('select[data-dept-switcher]').forEach(function (sel) {
          sel.value = MEN_URL;
        });
      } catch (e) { /* storage blocked: keep the WOMEN default */ }
    }

    // function init() {
    //   injectBackArrowStyle();
    //   placeTabletGenderSwitcher();
    //   initGenderSwitcherToggle();
    //   initClearClosesSearch();
    //   initScrollLock();
    // }

    function init() {
      injectBackArrowStyle();
      initDepartmentMemory();
      placeTabletGenderSwitcher();
      initGenderSwitcherToggle();
      initClearClosesSearch();
      initScrollLock();
      initCompactHeader();
      initEmptyBadges();
    }

    /* The corrective style should apply as early as possible; the rest
       waits for the DOM. injectBackArrowStyle is safe to call twice. */
    injectBackArrowStyle();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }

  })();